'use client'

import { useState, useRef, useEffect, useCallback } from 'react'
import Link from 'next/link'
import { File as FileIcon, Search, Signature as SignatureIcon, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { ThankYouMark } from '@/components/forms/ThankYouMark'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { StepIndicator } from '@/components/appraisal/StepIndicator'
import {
  AddressSummaryButton,
  StepActions,
  StepError,
  StepPrompt,
} from '@/components/appraisal/StepParts'
import { omitContact, reportToLeadCentre } from '@/utilities/leadCentre'

/**
 * Offer To Purchase Form — Figma section 4035:149956 ("Form - Offer To Purchase Flow").
 *
 * 6 steps, all sharing the appraisal wizard's chrome (progress bar → address
 * summary → prompt → controls → buttons):
 *   1. Property address                             (Figma 4035:149957)
 *   2. Your details + current residential address   (Figma 4095:85112)
 *   3. Purchaser terms: asking price / initial offer / deposit, settlement DATE,
 *      special conditions Yes→checkboxes→Other text (Figma 4095:86527)
 *   4. Document upload dropzone                     (Figma 4096:89309)
 *   5. Signature pad + today's date + privacy note  (Figma 4096:90910, CTA "Review")
 *   6. Review, one block per section with its own "Change?" → "Finalise & Send"
 *      (Figma 4096:92412)
 *
 * Rendered as a single page with client-side step state (no per-step routes),
 * matching the self-contained Figma CTA Block frame. When `initialAddress` is
 * supplied (the property page's "Make An Offer" CTA links here with the listing
 * address), step 1 is pre-answered and the flow opens at step 2.
 */

const TOTAL_STEPS = 6

interface AddressSuggestion {
  sla: string
  score: number
  pid: string
}

const CONDITION_OPTIONS = [
  { value: 'finance', label: 'Subject to Finance' },
  { value: 'building-pest', label: 'Subject to Building & Pest Inspection' },
  { value: 'subject-to-sale', label: 'Subject to Sale of Own Home' },
  { value: 'other', label: 'Other' },
]

interface FormState {
  address: string
  firstName: string
  middleName: string
  lastName: string
  email: string
  mobile: string
  residentialAddress: string
  askingPrice: string
  offerAmount: string
  depositAmount: string
  settlementDate: string
  hasConditions: '' | 'yes' | 'no'
  conditions: string[]
  message: string
  signedDate: string
}

const todayISO = () => new Date().toISOString().slice(0, 10)

/**
 * Field label — the canonical uppercase Body/bodyXs Label, identical to the Payload
 * form fields and the appraisal wizard.
 */
export function FieldLabel({ children, htmlFor }: { children: React.ReactNode; htmlFor: string }) {
  return (
    <Label htmlFor={htmlFor} className="mb-2">
      {children}
    </Label>
  )
}

/**
 * Address input with /api/address-lookup autocomplete — used for the property
 * address (step 1) and the buyer's residential address (step 2). Figma `menu item`
 * 2198:36854: theme/background surface, radius sm, Body/bodySm rows, highlighted
 * row = theme/secondary.
 */
export function AddressAutocomplete({
  id,
  value,
  placeholder,
  onValueChange,
  onEnter,
  national = false,
}: {
  id?: string
  value: string
  placeholder: string
  onValueChange: (v: string) => void
  onEnter?: () => void
  /** Search Australia-wide instead of VIC-only — interstate buyers' home address */
  national?: boolean
}) {
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const [highlightIndex, setHighlightIndex] = useState(-1)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) setIsOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  const fetchSuggestions = useCallback(
    (query: string) => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
      if (query.length < 3) {
        setSuggestions([])
        setIsOpen(false)
        return
      }
      debounceRef.current = setTimeout(async () => {
        try {
          const res = await fetch(
            `/api/address-lookup?q=${encodeURIComponent(query)}${national ? '&scope=au' : ''}`,
          )
          const data: AddressSuggestion[] = await res.json()
          setSuggestions(data.slice(0, 8))
          setIsOpen(data.length > 0)
          setHighlightIndex(-1)
        } catch {
          setSuggestions([])
          setIsOpen(false)
        }
      }, 250)
    },
    [national],
  )

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlightIndex((p) => Math.min(p + 1, suggestions.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlightIndex((p) => Math.max(p - 1, -1))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (highlightIndex >= 0 && suggestions[highlightIndex]) {
        onValueChange(suggestions[highlightIndex].sla)
        setSuggestions([])
        setIsOpen(false)
      } else {
        onEnter?.()
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false)
    }
  }

  return (
    <div ref={wrapperRef} className="relative">
      <Input
        id={id}
        type="text"
        value={value}
        onChange={(e) => {
          onValueChange(e.target.value)
          fetchSuggestions(e.target.value)
        }}
        onKeyDown={handleKeyDown}
        onFocus={() => {
          if (suggestions.length > 0) setIsOpen(true)
        }}
        placeholder={placeholder}
        autoComplete="off"
        className="pr-10"
      />
      <Search
        aria-hidden="true"
        className="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-foreground"
      />
      {isOpen && suggestions.length > 0 && (
        <ul className="absolute left-0 right-0 top-full z-50 mt-1 max-h-64 overflow-y-auto rounded-sm border border-border bg-background shadow-xs">
          {suggestions.map((s, i) => (
            <li
              key={s.pid}
              onMouseDown={(e) => {
                e.preventDefault()
                onValueChange(s.sla)
                setSuggestions([])
                setIsOpen(false)
              }}
              onMouseEnter={() => setHighlightIndex(i)}
              className={`cursor-pointer px-2 py-1.5 text-left text-body-sm font-light text-foreground ${
                i === highlightIndex ? 'bg-secondary' : 'bg-background'
              }`}
            >
              {s.sla}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

/**
 * Signature pad — Figma `Input Signature textarea` 2357:90056: 128px-tall
 * theme/muted surface, radius sm, "Sign here" hint with the Lucide signature
 * glyph, a hairline above an uppercase CLEAR affordance bottom-right.
 */
export function SignaturePad({
  value,
  onChange,
}: {
  value: string | null
  onChange: (dataUrl: string | null) => void
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const drawing = useRef(false)
  const [hasInk, setHasInk] = useState(Boolean(value))

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const dpr = window.devicePixelRatio || 1
    const rect = canvas.getBoundingClientRect()
    canvas.width = rect.width * dpr
    canvas.height = rect.height * dpr
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.scale(dpr, dpr)
    ctx.lineWidth = 2
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.strokeStyle = '#000000'
  }, [])

  const pointFromEvent = (e: React.PointerEvent) => {
    const rect = canvasRef.current!.getBoundingClientRect()
    return { x: e.clientX - rect.left, y: e.clientY - rect.top }
  }

  const start = (e: React.PointerEvent) => {
    e.preventDefault()
    canvasRef.current?.setPointerCapture(e.pointerId)
    drawing.current = true
    const ctx = canvasRef.current?.getContext('2d')
    if (!ctx) return
    const { x, y } = pointFromEvent(e)
    ctx.beginPath()
    ctx.moveTo(x, y)
  }

  const move = (e: React.PointerEvent) => {
    if (!drawing.current) return
    const ctx = canvasRef.current?.getContext('2d')
    if (!ctx) return
    const { x, y } = pointFromEvent(e)
    ctx.lineTo(x, y)
    ctx.stroke()
    if (!hasInk) setHasInk(true)
  }

  const end = () => {
    if (!drawing.current) return
    drawing.current = false
    const canvas = canvasRef.current
    if (canvas) onChange(canvas.toDataURL('image/png'))
  }

  const clear = () => {
    const canvas = canvasRef.current
    const ctx = canvas?.getContext('2d')
    if (canvas && ctx) ctx.clearRect(0, 0, canvas.width, canvas.height)
    setHasInk(false)
    onChange(null)
  }

  return (
    <div className="flex h-32 w-full flex-col rounded-sm bg-muted px-3 py-2 shadow-xs">
      <div className="relative min-h-0 flex-1">
        <canvas
          ref={canvasRef}
          className="absolute inset-0 h-full w-full cursor-crosshair touch-none"
          onPointerDown={start}
          onPointerMove={move}
          onPointerUp={end}
          onPointerLeave={end}
          aria-label="Signature pad"
        />
        {!hasInk && (
          <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-1.5 text-muted-foreground">
            <SignatureIcon className="size-6" aria-hidden="true" />
            <span className="text-body-sm font-light">Sign here</span>
          </div>
        )}
      </div>
      <div className="flex flex-col items-end gap-2">
        <div className="h-px w-full bg-muted-foreground/40" />
        <button
          type="button"
          onClick={clear}
          className="text-body-xs font-light uppercase text-muted-foreground transition-opacity hover:opacity-70"
        >
          Clear
        </button>
      </div>
    </div>
  )
}

/**
 * Review screen section — Figma "Search" groups (4096:93653 ff.), gap/gap-2 8px:
 *   title  = Body/bodyMdAlt — Lato regular 16 / line-height 24
 *   rows   = Body/bodyMd    — Lato LIGHT  16 / 24, label + value in a flex row
 *            with gap/gap-2 8px (label nowrap, value fills)
 *   Change? = `link button` 2198:34732 — Lato regular 16 / 24, underlined
 */
export function ReviewSection({
  title,
  rows,
  onChange,
}: {
  title: string
  rows: [string, string][]
  onChange: () => void
}) {
  return (
    <div className="flex w-full flex-col items-start gap-2">
      <p className="text-body-md font-normal text-foreground">{title}</p>
      {rows.map(([label, value]) => (
        <div
          key={label || value}
          className="flex w-full items-start gap-2 text-body-md font-light text-foreground"
        >
          {label && <p className="shrink-0 whitespace-nowrap">{label}:</p>}
          <p className="min-w-0 flex-1 break-words">{value}</p>
        </div>
      ))}
      <button
        type="button"
        onClick={onChange}
        className="text-body-md font-normal text-foreground underline transition-opacity hover:opacity-70"
      >
        Change?
      </button>
    </div>
  )
}

export function OfferForm({
  initialAddress,
  initialAskingPrice,
}: {
  initialAddress?: string
  /** Listing price carried in from the property page — locks the asking-price field */
  initialAskingPrice?: string
}) {
  const [step, setStep] = useState(initialAddress ? 2 : 1)
  const [form, setForm] = useState<FormState>(() => ({
    address: initialAddress || '',
    firstName: '',
    middleName: '',
    lastName: '',
    email: '',
    mobile: '',
    residentialAddress: '',
    askingPrice: initialAskingPrice || '',
    offerAmount: '',
    depositAmount: '',
    settlementDate: '',
    hasConditions: '',
    conditions: [],
    message: '',
    signedDate: todayISO(),
  }))
  const [files, setFiles] = useState<File[]>([])
  const [signature, setSignature] = useState<string | null>(null)
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [done, setDone] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [dragOver, setDragOver] = useState(false)

  const update = (patch: Partial<FormState>) => {
    setForm((prev) => ({ ...prev, ...patch }))
    setError('')
  }

  const goTo = (s: number) => {
    setError('')
    setStep(s)
  }

  const validateStep = (s: number): string | null => {
    if (s === 1 && !form.address.trim()) return 'Please enter the property address.'
    if (s === 2) {
      if (!form.firstName.trim() || !form.lastName.trim())
        return 'Please enter your first and last name.'
      if (!/^\S[^\s@]*@\S+\.\S+$/.test(form.email.trim())) return 'A valid email is required.'
      if (!/^[\d\s+()-]{8,15}$/.test(form.mobile.trim()))
        return 'A valid mobile number is required.'
      if (!form.residentialAddress.trim()) return 'Please enter your current residential address.'
    }
    if (s === 3) {
      if (!form.offerAmount.trim()) return 'Please enter your initial offer.'
      if (!form.depositAmount.trim()) return 'Please enter your deposit amount.'
      if (!form.hasConditions) return 'Please tell us whether your offer has special conditions.'
      if (form.hasConditions === 'yes' && form.conditions.length === 0)
        return 'Please select at least one special condition.'
    }
    if (s === 5 && !signature) return 'Please sign to continue.'
    return null
  }

  const next = () => {
    const problem = validateStep(step)
    if (problem) {
      setError(problem)
      return
    }
    setError('')
    setStep((s) => Math.min(s + 1, TOTAL_STEPS))
  }

  const back = () => {
    setError('')
    setStep((s) => Math.max(s - 1, 1))
  }

  const addFiles = (list: FileList | File[]) => {
    const incoming = Array.from(list)
    setFiles((prev) => [...prev, ...incoming].slice(0, 10))
  }

  const submit = async () => {
    setSubmitting(true)
    setError('')
    try {
      const { hasConditions, ...rest } = form
      const body = new FormData()
      body.append(
        'payload',
        JSON.stringify({
          ...rest,
          conditions: hasConditions === 'yes' ? form.conditions : [],
          signature: signature || undefined,
        }),
      )
      for (const file of files) body.append('documents', file)
      const res = await fetch('/api/offer-requests', { method: 'POST', body })
      if (!res.ok) throw new Error('submit failed')
      setDone(true)

      reportToLeadCentre({
        name: [form.firstName, form.middleName, form.lastName].filter(Boolean).join(' '),
        email: form.email,
        phone: form.mobile,
        leadType: 'BUYER_ENQUIRY',
        sourceForm: 'offer-to-purchase',
        fields: {
          ...omitContact(form, ['firstName', 'middleName', 'lastName', 'email', 'mobile']),
          conditions: hasConditions === 'yes' ? form.conditions : [],
        },
      })
    } catch {
      setError('Something went wrong submitting your offer. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  if (done) {
    return (
      <div className="flex flex-col items-start gap-6">
        <StepIndicator currentStep={TOTAL_STEPS} totalSteps={TOTAL_STEPS} />
        {/* Asana 018 — shared thank-you mark on all form success states */}
        <ThankYouMark />
        <h2 className="font-display text-display-2xl font-normal text-foreground">
          Thank you — your offer has been received.
        </h2>
        <p className="max-w-xl text-body-md font-light text-foreground">
          A member of the Wilson Property team will be in touch shortly to discuss your offer on{' '}
          <span className="font-normal">{form.address}</span>.
        </p>
        <Button asChild>
          <Link href="/buy">Browse More Properties</Link>
        </Button>
      </div>
    )
  }

  const selectedConditionLabels = form.conditions
    .map((c) => CONDITION_OPTIONS.find((o) => o.value === c)?.label || c)
    .join(', ')

  return (
    <div className="flex flex-col items-start gap-8">
      <StepIndicator currentStep={step} totalSteps={TOTAL_STEPS} />

      {/* Address summary visible from step 2 onward — Figma Frame 753 */}
      {step > 1 && step < TOTAL_STEPS && (
        <AddressSummaryButton address={form.address} onChange={() => goTo(1)} />
      )}

      {/* STEP 1 — Property address */}
      {step === 1 && (
        <div className="w-full">
          <StepPrompt>What is the address you would like to purchase?</StepPrompt>
          <AddressAutocomplete
            value={form.address}
            placeholder="Search for the address"
            onValueChange={(v) => update({ address: v })}
            onEnter={next}
          />
          {error && <StepError>{error}</StepError>}
          <StepActions>
            <Button onClick={next}>Start</Button>
          </StepActions>
        </div>
      )}

      {/* STEP 2 — Your details + current residential address (Figma 4095:85112) */}
      {step === 2 && (
        <div className="w-full">
          <StepPrompt>Please confirm your details.</StepPrompt>
          <div className="flex flex-col gap-6">
            <div>
              <FieldLabel htmlFor="offer-firstName">First Name *</FieldLabel>
              <Input
                id="offer-firstName"
                value={form.firstName}
                onChange={(e) => update({ firstName: e.target.value })}
                placeholder="e.g. Alex"
              />
            </div>
            <div>
              <FieldLabel htmlFor="offer-middleName">Middle Name</FieldLabel>
              <Input
                id="offer-middleName"
                value={form.middleName}
                onChange={(e) => update({ middleName: e.target.value })}
                placeholder="e.g. Riley"
              />
            </div>
            <div>
              <FieldLabel htmlFor="offer-lastName">Last Name *</FieldLabel>
              <Input
                id="offer-lastName"
                value={form.lastName}
                onChange={(e) => update({ lastName: e.target.value })}
                placeholder="e.g. Smith"
              />
            </div>
            <div>
              <FieldLabel htmlFor="offer-email">Email *</FieldLabel>
              <Input
                id="offer-email"
                type="email"
                value={form.email}
                onChange={(e) => update({ email: e.target.value })}
                placeholder="your@email.com"
              />
            </div>
            <div>
              <FieldLabel htmlFor="offer-mobile">Mobile *</FieldLabel>
              <Input
                id="offer-mobile"
                type="tel"
                value={form.mobile}
                onChange={(e) => update({ mobile: e.target.value })}
                placeholder="04XX XXX XXX"
              />
            </div>
            <div>
              <p className="mb-6 text-body-md font-normal text-foreground">
                Your current residential address. *
              </p>
              <AddressAutocomplete
                id="offer-residential"
                national
                value={form.residentialAddress}
                placeholder="Type your address"
                onValueChange={(v) => update({ residentialAddress: v })}
              />
            </div>
          </div>
          {error && <StepError>{error}</StepError>}
          <StepActions>
            <Button onClick={next}>Next</Button>
            <Button variant="ghost" onClick={back}>
              Back
            </Button>
          </StepActions>
        </div>
      )}

      {/* STEP 3 — Purchaser terms (Figma 4095:86527) */}
      {step === 3 && (
        <div className="w-full">
          <StepPrompt>Proposed purchaser terms.</StepPrompt>
          <div className="flex flex-col gap-6">
            <div>
              <FieldLabel htmlFor="offer-asking">Property Asking Price</FieldLabel>
              <Input
                id="offer-asking"
                inputMode="numeric"
                value={form.askingPrice}
                onChange={(e) => update({ askingPrice: e.target.value })}
                placeholder="$"
                readOnly={Boolean(initialAskingPrice)}
                aria-readonly={Boolean(initialAskingPrice)}
                className={initialAskingPrice ? 'pointer-events-none' : undefined}
                tabIndex={initialAskingPrice ? -1 : undefined}
              />
            </div>
            <div>
              <FieldLabel htmlFor="offer-amount">Buyer&rsquo;s Initial Offer *</FieldLabel>
              <Input
                id="offer-amount"
                inputMode="numeric"
                value={form.offerAmount}
                onChange={(e) => update({ offerAmount: e.target.value })}
                placeholder="$"
              />
            </div>
            <div>
              <FieldLabel htmlFor="offer-deposit">Buyer&rsquo;s Deposit Amount *</FieldLabel>
              <Input
                id="offer-deposit"
                inputMode="numeric"
                value={form.depositAmount}
                onChange={(e) => update({ depositAmount: e.target.value })}
                placeholder="$"
              />
            </div>
            <div>
              <p className="mb-6 text-body-md font-normal text-foreground">
                Proposed settlement date.
              </p>
              <Input
                type="date"
                aria-label="Proposed settlement date"
                value={form.settlementDate}
                onChange={(e) => update({ settlementDate: e.target.value })}
                className="w-[248px]"
              />
            </div>
            <div>
              <p className="mb-6 text-body-md font-normal text-foreground">
                Any special conditions? *
              </p>
              <RadioGroup
                name="offer-has-conditions"
                value={form.hasConditions}
                onValueChange={(v) => update({ hasConditions: v as 'yes' | 'no' })}
                className="flex flex-col gap-3"
              >
                {(['yes', 'no'] as const).map((v) => (
                  <label key={v} className="flex cursor-pointer items-center gap-2.5">
                    <RadioGroupItem value={v} />
                    <span className="text-body-sm font-normal text-foreground">
                      {v === 'yes' ? 'Yes' : 'No'}
                    </span>
                  </label>
                ))}
              </RadioGroup>
            </div>
            {form.hasConditions === 'yes' && (
              <div>
                <p className="mb-6 text-body-md font-normal text-foreground">
                  Select all special conditions that apply.
                </p>
                <div className="flex flex-col gap-3">
                  {CONDITION_OPTIONS.map((opt) => (
                    <label key={opt.value} className="flex cursor-pointer items-center gap-3">
                      <Checkbox
                        checked={form.conditions.includes(opt.value)}
                        onCheckedChange={(checked) =>
                          update({
                            conditions: checked
                              ? [...form.conditions, opt.value]
                              : form.conditions.filter((c) => c !== opt.value),
                          })
                        }
                      />
                      <span className="text-body-sm font-normal text-foreground">{opt.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            {form.hasConditions === 'yes' && form.conditions.includes('other') && (
              <div>
                <p className="mb-6 text-body-md font-normal text-foreground">
                  Please specify your other special conditions.
                </p>
                <FieldLabel htmlFor="offer-notes">Your Message</FieldLabel>
                <Textarea
                  id="offer-notes"
                  value={form.message}
                  onChange={(e) => update({ message: e.target.value })}
                  rows={3}
                  placeholder="Type your message here…"
                />
              </div>
            )}
          </div>
          {error && <StepError>{error}</StepError>}
          <StepActions>
            <Button onClick={next}>Next</Button>
            <Button variant="ghost" onClick={back}>
              Back
            </Button>
          </StepActions>
        </div>
      )}

      {/* STEP 4 — Documents (Figma 4096:89309) */}
      {step === 4 && (
        <div className="w-full">
          <StepPrompt>Please upload any relevant documents</StepPrompt>
          <div className="flex flex-col gap-2">
            <Label asChild>
              <span>
                Pre-qualification letter from lender, loan commitment letter from lender etc.
              </span>
            </Label>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => {
                e.preventDefault()
                setDragOver(true)
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault()
                setDragOver(false)
                addFiles(e.dataTransfer.files)
              }}
              className={`flex h-16 w-full flex-col items-center justify-center gap-1.5 rounded-sm px-3 py-2 shadow-xs transition-colors ${
                dragOver ? 'bg-secondary' : 'bg-muted'
              }`}
            >
              <FileIcon className="size-6 text-muted-foreground" aria-hidden="true" />
              <span className="text-body-sm font-light text-muted-foreground">
                Select or drag and drop files.
              </span>
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".pdf,.doc,.docx,image/*"
              className="hidden"
              onChange={(e) => {
                if (e.target.files) addFiles(e.target.files)
                e.target.value = ''
              }}
            />
            {files.length > 0 && (
              <ul className="mt-2 flex w-full flex-col gap-1.5">
                {files.map((file, i) => (
                  <li
                    key={`${file.name}-${i}`}
                    className="flex items-center justify-between gap-3 rounded-sm border border-border px-3 py-1.5"
                  >
                    <span className="truncate text-body-sm font-light text-foreground">
                      {file.name}
                    </span>
                    <button
                      type="button"
                      onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))}
                      aria-label={`Remove ${file.name}`}
                      className="shrink-0 text-muted-foreground transition-opacity hover:opacity-70"
                    >
                      <X className="size-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          {error && <StepError>{error}</StepError>}
          <StepActions>
            <Button onClick={next}>Next</Button>
            <Button variant="ghost" onClick={back}>
              Back
            </Button>
          </StepActions>
        </div>
      )}

      {/* STEP 5 — Signature (Figma 4096:90910) */}
      {step === 5 && (
        <div className="w-full">
          <StepPrompt>Signed for and on behalf of the Registrant. *</StepPrompt>
          <div className="flex flex-col gap-6">
            <SignaturePad value={signature} onChange={setSignature} />
            <div>
              <p className="mb-6 text-body-md font-normal text-foreground">Today&rsquo;s date.</p>
              <Input
                type="date"
                aria-label="Today's date"
                value={form.signedDate}
                onChange={(e) => update({ signedDate: e.target.value })}
                className="w-[248px]"
              />
            </div>
            <p className="text-body-md font-light text-foreground">
              By submitting this form, you agree to our{' '}
              <Link
                href="/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
              >
                Privacy Policy
              </Link>
              .
            </p>
          </div>
          {error && <StepError>{error}</StepError>}
          <StepActions>
            <Button onClick={next}>Review</Button>
            <Button variant="ghost" onClick={back}>
              Back
            </Button>
          </StepActions>
        </div>
      )}

      {/* STEP 6 — Review (Figma 4096:92412) — one block per section, each with Change? */}
      {step === 6 && (
        <div className="flex w-full flex-col gap-8">
          <ReviewSection
            title="The address you would like purchase?"
            rows={[['', form.address]]}
            onChange={() => goTo(1)}
          />
          <ReviewSection
            title="Your details:"
            rows={[
              ['First name', form.firstName],
              ...(form.middleName ? [['Middle name', form.middleName] as [string, string]] : []),
              ['Last name', form.lastName],
              ['Email', form.email],
              ['Mobile', form.mobile],
            ]}
            onChange={() => goTo(2)}
          />
          <ReviewSection
            title="Your current residential address:"
            rows={[['', form.residentialAddress]]}
            onChange={() => goTo(2)}
          />
          <ReviewSection
            title="Your purchase terms:"
            rows={[
              ...(form.askingPrice
                ? [['Property Asking Price', form.askingPrice] as [string, string]]
                : []),
              ['Buyer’s Initial Offer', form.offerAmount],
              ['Buyer’s Deposit Amount', form.depositAmount],
              ...(form.settlementDate
                ? [['Proposed settlement date', form.settlementDate] as [string, string]]
                : []),
            ]}
            onChange={() => goTo(3)}
          />
          <ReviewSection
            title="Special conditions:"
            rows={
              form.hasConditions === 'yes'
                ? [
                    ['', 'Yes'],
                    ['Conditions', selectedConditionLabels],
                    ...(form.message ? [['Other', form.message] as [string, string]] : []),
                  ]
                : [['', 'No']]
            }
            onChange={() => goTo(3)}
          />
          <ReviewSection
            title="Uploaded documents:"
            rows={
              files.length
                ? files.map((f) => ['', f.name] as [string, string])
                : [['', 'None uploaded']]
            }
            onChange={() => goTo(4)}
          />
          <ReviewSection
            title="Signed:"
            rows={[['', signature ? 'Yes' : 'No']]}
            onChange={() => goTo(5)}
          />
          {error && <StepError>{error}</StepError>}
          <StepActions>
            <Button onClick={submit} loading={submitting}>
              Finalise &amp; Send
            </Button>
            <Button variant="ghost" onClick={back} disabled={submitting}>
              Back
            </Button>
          </StepActions>
        </div>
      )}
    </div>
  )
}
